(function () {
  const SECTION_CLASS = "banner-grid-section";
  const ROOT_SELECTOR = ".banner-grid";
  const CARD_SELECTOR = ".banner-grid-card";
  const INNER_SELECTOR = ".banner-grid-card__inner";
  const HEADING_SELECTOR = ":scope > .block-heading";

  // The hover reveal slides the content panel up by exactly the height of the blocks
  // hidden below the heading, so the card keeps its size and nothing is clipped. That
  // height depends on the merchant's copy, so it is measured per card and published as
  // --banner-grid-reveal-shift (see src/scss/sections/section-banner-grid.scss).
  class BannerGridReveal {
    constructor(root) {
      this.root = root;
      this.cards = Array.from(root.querySelectorAll(CARD_SELECTOR));
      this.raf = null;
      this.observer = new ResizeObserver(() => this._scheduleMeasure());

      this.cards.forEach((card) => {
        this.observer.observe(card);
        this._hiddenBlocks(card).forEach((block) => this.observer.observe(block));
      });

      this._measure();
    }

    destroy() {
      this.observer.disconnect();
      if (this.raf) {
        cancelAnimationFrame(this.raf);
        this.raf = null;
      }
      this.cards.forEach((card) => {
        card.style.removeProperty("--banner-grid-reveal-shift");
      });
    }

    _heading(card) {
      const inner = card.querySelector(INNER_SELECTOR);
      if (!inner) return null;
      return inner.querySelector(HEADING_SELECTOR);
    }

    _hiddenBlocks(card) {
      const heading = this._heading(card);
      if (!heading) return [];

      const blocks = [];
      let node = heading.nextElementSibling;
      while (node) {
        blocks.push(node);
        node = node.nextElementSibling;
      }
      return blocks;
    }

    _scheduleMeasure() {
      if (this.raf) return;
      this.raf = requestAnimationFrame(() => {
        this.raf = null;
        this._measure();
      });
    }

    _measure() {
      this.cards.forEach((card) => {
        // An open card is mid-width-transition, so measuring it would chase the
        // animation — keep the value its idle layout produced.
        if (card.matches(":hover, :focus-within")) return;

        const heading = this._heading(card);
        const blocks = this._hiddenBlocks(card);
        const last = blocks[blocks.length - 1];

        if (!heading || !last) {
          card.style.removeProperty("--banner-grid-reveal-shift");
          return;
        }

        // offsetTop/offsetHeight, not rects: the group carries a slide-in transform that
        // a rect would fold into the distance. Both blocks are children of the same
        // static panel, so their offsets share an origin and cancel out — what is left is
        // the group's laid-out height plus the gaps between its blocks.
        const shift =
          last.offsetTop +
          last.offsetHeight -
          heading.offsetTop -
          heading.offsetHeight;

        card.style.setProperty(
          "--banner-grid-reveal-shift",
          `${Math.max(Math.round(shift), 0)}px`
        );
      });
    }
  }

  function destroyRoot(root) {
    if (!root._bannerGridReveal) return;
    root._bannerGridReveal.destroy();
    root._bannerGridReveal = null;
  }

  function initSection(section) {
    if (!section || !section.classList?.contains(SECTION_CLASS)) return;

    const root = section.querySelector(ROOT_SELECTOR);
    if (!root) return;

    destroyRoot(root);
    if (root.dataset.hoverEffect !== "true") return;

    root._bannerGridReveal = new BannerGridReveal(root);
  }

  initSection(document.currentScript.parentElement);

  if (!window.__themeBannerGridEvents) {
    window.__themeBannerGridEvents = true;

    document.addEventListener("shopify:section:load", (event) => {
      initSection(event.target);
    });

    document.addEventListener("shopify:section:unload", (event) => {
      event.target.querySelectorAll(ROOT_SELECTOR).forEach(destroyRoot);
    });
  }
})();
